import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Home, Search, Plus, MessageCircle, User, LogOut, Store, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { isUserAdmin } from "@/lib/api";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/915628ec-c769-483a-a989-edeba52edaef/images/5a443e7bbd327d6637112bc60802e461084c24e59ef2ec26d6aac8546f4c5bc7.jpeg";
export { LOGO_URL };

const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");

  const submit = (e) => {
    e.preventDefault();
    navigate(`/?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-3">
        <Link to="/" data-testid="app-header-logo" className="flex items-center gap-2 shrink-0">
          <img src={LOGO_URL} alt="Kalabiru" className="w-9 h-9 rounded-xl object-cover" />
          <div className="leading-tight hidden sm:block">
            <div className="font-extrabold text-[#0B4DA2] text-lg tracking-tight">Kalabiru</div>
            <div className="text-[10px] text-gray-400 -mt-1">Preloved Marketplace</div>
          </div>
        </Link>

        <form onSubmit={submit} className="flex-1 max-w-xl">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="search-input-field"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari barang preloved..."
              className="w-full pl-9 pr-4 py-2.5 rounded-full bg-gray-100 focus:bg-white border border-transparent focus:border-[#DBEAFE] outline-none text-sm transition"
            />
          </div>
        </form>

        <div className="hidden md:flex items-center gap-2">
          {user && (user.is_admin || isUserAdmin(user)) && (
            <button
              data-testid="topbar-admin-button"
              onClick={() => navigate("/admin")}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 text-[#0B4DA2] hover:bg-blue-100 text-xs font-bold transition border border-blue-200"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Admin</span>
            </button>
          )}
          <button
            data-testid="topbar-sell-button"
            onClick={() => navigate("/sell")}
            className="flex items-center gap-1.5 bg-[#0B4DA2] hover:bg-[#083B7C] text-white px-4 py-2.5 rounded-full text-sm font-semibold transition"
          >
            <Plus className="w-4 h-4" /> Jual
          </button>
          <button data-testid="topbar-chat-button" onClick={() => navigate("/chat")} className="p-2.5 rounded-full hover:bg-gray-100 transition">
            <MessageCircle className="w-5 h-5 text-gray-600" />
          </button>
          {user ? (
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/profile")} data-testid="topbar-profile-button">
                <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[#DBEAFE]" />
              </button>
              <button data-testid="topbar-logout-button" onClick={logout} className="p-2 rounded-full hover:bg-gray-100">
                <LogOut className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <button data-testid="topbar-login-button" onClick={() => navigate("/login")} className="px-4 py-2.5 rounded-full border border-[#0B4DA2] text-[#0B4DA2] text-sm font-semibold">
              Masuk
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const active = (path) => location.pathname === path;
  const cls = (path) => `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${active(path) ? "text-[#0B4DA2]" : "text-gray-400"}`;

  const items = [
    { path: "/", icon: Home, label: "Home", testid: "nav-home-button" },
    { path: "/?explore=1", icon: Search, label: "Jelajah", testid: "nav-explore-button", match: "/explore" },
    { path: "/chat", icon: MessageCircle, label: "Chat", testid: "nav-chat-button" },
    { path: user ? "/profile" : "/login", icon: User, label: "Profil", testid: "nav-profile-button" },
  ];

  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-100 pb-safe">
      <div className="grid grid-cols-5 h-16 items-center relative">
        <button className={cls("/")} data-testid="nav-home-button" onClick={() => navigate("/")}>
          <Home className="w-5 h-5" /> Home
        </button>
        <button className={cls("/search")} data-testid="nav-explore-button" onClick={() => navigate("/?explore=1")}>
          <Search className="w-5 h-5" /> Jelajah
        </button>
        <div className="flex justify-center">
          <button
            data-testid="nav-sell-button"
            onClick={() => navigate("/sell")}
            className="w-14 h-14 -mt-6 rounded-full brand-gradient text-white flex items-center justify-center shadow-lg shadow-[#0B4DA2]/40 border-4 border-white"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
        <button className={cls("/chat")} data-testid="nav-chat-button" onClick={() => navigate("/chat")}>
          <MessageCircle className="w-5 h-5" /> Chat
        </button>
        <button className={cls("/profile")} data-testid="nav-profile-button" onClick={() => navigate(user ? "/profile" : "/login")}>
          <User className="w-5 h-5" /> Profil
        </button>
      </div>
    </nav>
  );
};

export default function Layout() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 pb-28 md:pb-10 pt-4">
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}
